import { useState, useEffect } from 'react';
import { Loader2, Check, Building2, AlertCircle } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { transactionsAPI } from '../lib/api';
import { formatCurrency } from '../lib/utils';
import { getErrorMessage } from '../lib/errors';
import { toast } from 'sonner';
import { format } from 'date-fns';

function fmtDate(d) {
  if (!d) return '—';
  try { return format(new Date(d), 'dd MMM yyyy'); } catch (e) { return String(d); }
}

/**
 * Reconcile a transaction that has no invoice counterpart, recording why.
 * A non-empty reason is required (the Apply button stays disabled until one is
 * entered; the backend also rejects an empty reason with 422). Apply sets the
 * existing is_reconciled flag plus reconciliation_method/reason, so no new
 * state is introduced.
 */
export function ReconcileWithoutInvoiceDialog({ transaction, open, onClose, onApplied }) {
  var [reason, setReason] = useState('');
  var [applying, setApplying] = useState(false);

  useEffect(function () {
    if (open) { setReason(''); setApplying(false); }
  }, [open, transaction]);

  var trimmed = reason.trim();

  var handleApply = function () {
    if (!transaction || !trimmed) return;
    setApplying(true);
    transactionsAPI.reconcileManual(transaction.id, trimmed)
      .then(function () {
        toast.success('Transaction reconciled without an invoice');
        setApplying(false);
        onApplied && onApplied();
      })
      .catch(function (err) {
        setApplying(false);
        toast.error(getErrorMessage(err));
      });
  };

  if (!transaction) return null;

  return (
    <Dialog open={open} onOpenChange={function (o) { if (!o) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="reconcile-no-invoice-dialog">
        <DialogHeader>
          <DialogTitle>Reconcile without invoice</DialogTitle>
          <DialogDescription>
            Mark this transaction reconciled with no invoice counterpart. A reason is
            required and stored for audit.
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border p-3 bg-muted/40 text-sm flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="flex items-center gap-1.5 font-medium">
            <Building2 className="h-4 w-4 text-primary" /> Transaction
          </span>
          <span className="text-muted-foreground">{fmtDate(transaction.date)}</span>
          <span className="font-mono font-medium">{formatCurrency(transaction.amount, transaction.currency)}</span>
          <span className="truncate max-w-[220px]">{transaction.counterparty || transaction.description || '—'}</span>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm font-medium">Reason</label>
          <Textarea
            value={reason}
            onChange={function (e) { setReason(e.target.value); }}
            placeholder="e.g. bank fee, internal transfer, no invoice expected"
            rows={3}
            data-testid="reconcile-reason"
          />
          {!trimmed && (
            <p className="flex items-center gap-1 text-xs text-muted-foreground">
              <AlertCircle className="h-3 w-3" /> A reason is required to reconcile without an invoice.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={applying}>Cancel</Button>
          <Button onClick={handleApply} disabled={!trimmed || applying} data-testid="reconcile-no-invoice-apply">
            {applying ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Check className="h-4 w-4 mr-1" />}
            Apply
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
